import { Link } from "react-router-dom";
import { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useT } from "@/context/ContentContext";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { toast, Toaster } from "sonner";
import {
  Smartphone, BellRing, MessageSquare, Sparkles, Apple, ChevronDown, ChevronRight, Bell, BellOff, Check, Loader2,
} from "lucide-react";

function AndroidIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.6 9.48l1.84-3.18a.4.4 0 00-.69-.4l-1.86 3.22a11.36 11.36 0 00-9.78 0L5.25 5.9a.4.4 0 00-.69.4l1.84 3.18A10.74 10.74 0 002 17.5h20a10.74 10.74 0 00-4.4-8.02zM7 14.5a1 1 0 110-2 1 1 0 010 2zm10 0a1 1 0 110-2 1 1 0 010 2z" />
    </svg>
  );
}

function StepCard({ n, title, children, testid }) {
  return (
    <div className="card-dark p-5 sm:p-6" data-testid={testid}>
      <div className="flex items-start gap-4">
        <div className="shrink-0 h-9 w-9 rounded-full grid place-items-center bg-gradient-to-br from-[#0055FF] to-[#00E5FF] text-white font-display text-lg">
          {n}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-varsity text-lg tracking-wider">{title}</div>
          <div className="mt-2 text-sm text-white/70 leading-relaxed">{children}</div>
        </div>
      </div>
    </div>
  );
}

function Collapsible({ title, icon: Icon, children, defaultOpen = false, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/10 rounded-lg overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left bg-white/3 hover:bg-white/5 transition-colors"
        data-testid={`${testid}-toggle`}
      >
        <Icon size={16} className="text-[#00E5FF]" />
        <span className="flex-1 font-varsity tracking-wider text-sm">{title}</span>
        {open ? <ChevronDown size={14} className="text-white/40" /> : <ChevronRight size={14} className="text-white/40" />}
      </button>
      {open && (
        <div className="px-4 pb-5 pt-2 text-sm text-white/75 leading-relaxed space-y-2">
          {children}
        </div>
      )}
    </div>
  );
}

export default function Welcome() {
  const t = useT();
  const push = usePushNotifications();

  const onEnablePush = async () => {
    const r = await push.enable();
    if (r.ok) {
      toast.success("Notifications turned on. We'll ping you when Coach posts something!");
    } else if (r.reason === "denied") {
      toast.error("Notifications were blocked. Open your browser settings to allow them for this site.");
    } else if (r.reason === "unsupported") {
      toast.error("This browser doesn't support push notifications. Try Safari (iOS) or Chrome (Android).");
    } else {
      toast.error("Couldn't enable notifications. Please try again.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <Toaster theme="dark" position="top-right" richColors />

      <main className="mx-auto max-w-3xl w-full px-6 py-14 flex-1">
        <div className="text-center">
          <div className="inline-flex h-16 w-16 rounded-full grid place-items-center bg-gradient-to-br from-[#0055FF] to-[#00E5FF] mb-5 animate-pulse-glow">
            <Sparkles size={26} className="text-white" />
          </div>
          <div className="overline mb-2">Onboarding</div>
          <h1 className="font-display uppercase text-4xl sm:text-5xl text-chrome">
            {t("welcome.title", "Welcome to the Family!")}
          </h1>
          <p className="mt-4 text-white/70 max-w-xl mx-auto leading-relaxed">
            {t("welcome.intro", "")}
          </p>
        </div>

        <div className="mt-12 space-y-4">
          {/* Step 1: add to home screen */}
          <StepCard n={1} title="Add this site to your home screen" testid="welcome-step-homescreen">
            Opening the portal as an "app" from your home screen is the fastest way to check announcements, messages, and the schedule. It also keeps you logged in.

            <div className="mt-4 space-y-3">
              <Collapsible title="iPhone / iPad — Safari" icon={Apple} defaultOpen testid="install-ios">
                <ol className="list-decimal pl-5 space-y-1.5">
                  <li>Make sure you&apos;re viewing this site in <span className="text-white">Safari</span> (not Chrome).</li>
                  <li>Tap the <span className="text-white">Share</span> icon at the bottom (the square with the arrow pointing up).</li>
                  <li>Scroll down and tap <span className="text-white">Add to Home Screen</span>.</li>
                  <li>Name it <span className="text-white">Dazzlin&apos; Dymes</span> and tap <span className="text-white">Add</span>.</li>
                  <li>The diamond logo will now appear on your home screen — tap it any time to jump straight in.</li>
                </ol>
              </Collapsible>

              <Collapsible title="Android — Chrome" icon={AndroidIcon} testid="install-android">
                <ol className="list-decimal pl-5 space-y-1.5">
                  <li>Tap the <span className="text-white">⋮ menu</span> in the top right of Chrome.</li>
                  <li>Tap <span className="text-white">Add to Home screen</span> (sometimes shown as <span className="text-white">Install app</span>).</li>
                  <li>Confirm the name and tap <span className="text-white">Add</span>.</li>
                  <li>You&apos;ll get a tile on your home screen that opens the portal in its own window.</li>
                </ol>
              </Collapsible>
            </div>
          </StepCard>

          {/* Step 2: notifications */}
          <StepCard n={2} title="Turn on notifications" testid="welcome-step-notifications">
            Coach will send you messages and post weekly announcements right here in the portal. To make sure you never miss one:

            <div className="mt-4 mb-4 card-dark p-4 border-[#00E5FF]/30 bg-[#00E5FF]/5">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full grid place-items-center bg-gradient-to-br from-[#0055FF] to-[#00E5FF] text-white shrink-0">
                  {push.subscribed ? <Check size={18} /> : <Bell size={18} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-varsity text-base tracking-wider text-white">
                    {push.subscribed ? "Notifications are ON" : "One-tap notifications"}
                  </div>
                  <div className="text-xs text-white/65 mt-1">
                    {!push.supported && "Your current browser doesn't support push notifications. Try opening this site in Safari on iPhone (16.4+) or Chrome on Android."}
                    {push.supported && push.permission === "denied" &&
                      "Notifications are blocked in your browser settings. Open your browser settings → Notifications → allow this site, then refresh."}
                    {push.supported && push.permission !== "denied" && !push.subscribed &&
                      "Tap the button below — your phone will ask once, just say Allow. You'll get a ping for new messages, announcements, and broadcasts from Coach."}
                    {push.supported && push.subscribed &&
                      "You're all set on this device. You can turn this off any time from your Account page."}
                  </div>
                  {push.supported && push.permission !== "denied" && !push.subscribed && (
                    <button onClick={onEnablePush} disabled={push.loading} className="btn-electric mt-3 !text-xs" data-testid="welcome-enable-push-btn">
                      {push.loading ? <><Loader2 className="animate-spin" size={13} /> Setting up…</> : <><BellRing size={13} /> Enable Notifications</>}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <ul className="list-disc pl-5 mt-3 space-y-1.5">
              <li>The <span className="text-white">cyan number</span> on the <span className="text-[#00E5FF] font-medium inline-flex items-center gap-1"><MessageSquare size={12} /> Messages</span> link in your top nav shows unread messages from Coach.</li>
              <li>On <span className="text-white">iOS</span>, push notifications only work once you&apos;ve added the site to your home screen (Step 1 above).</li>
              <li>You can change this setting later under <span className="text-white">Account → Notifications</span>.</li>
            </ul>
          </StepCard>

          {/* Step 3: tour */}
          <StepCard n={3} title="Your quick portal tour" testid="welcome-step-tour">
            Here&apos;s what you&apos;ll use most often:

            <ul className="list-disc pl-5 mt-3 space-y-1.5">
              <li><span className="text-white">Dashboard</span> — weekly announcements from Coach + upcoming events.</li>
              <li><span className="text-white">Messages</span> — direct private chat with Coach.</li>
              <li><span className="text-white">Events</span> — full practice / performance / competition schedule.</li>
              <li><span className="text-white">Tuition + Shop</span> — see your plan, browse team gear.</li>
              <li><span className="text-white">Account</span> (your name top-right) — change your password.</li>
            </ul>
          </StepCard>
        </div>

        <div className="mt-10 text-center">
          <Link to="/dashboard" className="btn-electric" data-testid="welcome-cta">
            {t("welcome.cta", "Take Me to My Dashboard")}
          </Link>
          <div className="mt-3 text-[11px] text-white/40">
            You can come back to these instructions any time at <span className="text-white/60">/welcome</span>.
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
